import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { EliminatingEntry } from "@common/schemas/domains/accounting/eliminatingEntry.schema";
import { Drawer } from "@design-system/Drawer/Drawer";
import { VStack } from "@design-system/VStack/VStack";
import { Loader } from "@design-system/Loader/Loader";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Entity } from "@common/types/entity";
import { useAccountsByEntity } from "@hooks/useAccounts";
import { useMemoByKeys } from "@hooks/useMemoByKeys";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useLockDateState } from "@api/lock.api";
import { getConsolidationErrorMessage } from "@api/consolidation.api";
import {
    useCreateEliminatingEntryMutation,
    useDeleteEliminatingEntryMutation,
    useUpdateEliminatingEntryMutation,
} from "@api/eliminatingEntries.api";
import { JournalEntryDrawerHeader } from "@components/accounting/journalEntryDrawer/JournalEntryDrawerHeader";
import { JournalEntryScope } from "@components/accounting/journalEntryDrawer/journalEntryScope";
import { JournalEntryConsolidationContext } from "@components/accounting/journalEntryDrawer/journalEntryConsolidationContext";
import {
    getFormValidity,
    initialFormValues,
    isEliminatingEntryEditLocked,
    toRequestBody,
} from "./eliminatingEntryDrawer.lib";
import { EliminatingEntryDrawerForm } from "./EliminatingEntryDrawerForm";
import { EliminatingEntrySubmitMode } from "./EliminatingEntryFormFooter";

interface EliminatingEntryDrawerProps {
    open: boolean;
    onClose: () => void;
    workspaceId: string;
    groupId: string;
    // Group member entities (parent first) whose charts the per-line account
    // picker draws from.
    entities: Entity[];
    entry?: EliminatingEntry;
    scope?: JournalEntryScope;
    scopeEntities?: readonly Entity[];
    consolidationContext?: JournalEntryConsolidationContext;
    onScopeChange?: (scope: JournalEntryScope) => void;
}

const EMPTY_ENTITY_IDS: number[] = [];

export const EliminatingEntryDrawer: React.FC<EliminatingEntryDrawerProps> = ({
    open,
    onClose,
    workspaceId,
    groupId,
    entities,
    entry,
    scope,
    scopeEntities,
    consolidationContext,
    onScopeChange,
}) => {
    const { toast } = useToaster();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [mutationError, setMutationError] = useState<string | null>(null);
    const [pendingSubmitMode, setPendingSubmitMode] =
        useState<EliminatingEntrySubmitMode | null>(null);
    const isSubmitInFlightRef = useRef(false);
    const drawerGenerationRef = useRef(0);
    const requestSequenceRef = useRef(0);
    const activeRequestIdRef = useRef<number | null>(null);
    const isOpenRef = useRef(open);
    isOpenRef.current = open;
    const isCurrentRequest = (drawerGeneration: number, requestId: number) =>
        isOpenRef.current &&
        drawerGenerationRef.current === drawerGeneration &&
        activeRequestIdRef.current === requestId;

    useEffect(() => {
        drawerGenerationRef.current += 1;
        activeRequestIdRef.current = null;
        isSubmitInFlightRef.current = false;
        if (open) {
            setMutationError(null);
            setPendingSubmitMode(null);
        }
    }, [open, entry?.id, groupId]);
    // Gate the chart-of-accounts fetches on `open`: the drawer is mounted
    // whenever authoring is possible, so passing an empty list while closed
    // keeps the accounts queries a no-op until the user first opens the drawer.
    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );
    const {
        accountsById,
        accountsByEntityId,
        isLoading: isLoadingAccounts,
    } = useAccountsByEntity(open ? entityIds : EMPTY_ENTITY_IDS);
    const { lockDate, isResolved: isLockStateResolved } = useLockDateState(
        entities[0]?.id,
    );
    const isEditLocked = isEliminatingEntryEditLocked({
        storedEntryDate: entry?.date,
        lockDate,
        isLockStateResolved,
    });

    useEffect(() => {
        if (isEditLocked) {
            setShowDeleteConfirm(false);
        }
    }, [isEditLocked]);

    const mutationParams = { workspaceId, groupId };
    const createMutation = useCreateEliminatingEntryMutation(mutationParams);
    const updateMutation = useUpdateEliminatingEntryMutation(mutationParams);
    const deleteMutation = useDeleteEliminatingEntryMutation(mutationParams);
    const handleDelete = () => {
        if (!entry || isEditLocked) {
            return Promise.resolve();
        }
        const drawerGeneration = drawerGenerationRef.current;
        const requestId = ++requestSequenceRef.current;
        activeRequestIdRef.current = requestId;
        return deleteMutation
            .mutateAsync(entry.id)
            .then(() => {
                if (!isCurrentRequest(drawerGeneration, requestId)) {
                    return;
                }
                activeRequestIdRef.current = null;
                toast("Eliminating entry deleted");
                setShowDeleteConfirm(false);
                onClose();
            })
            .catch((mutationFailure) => {
                if (!isCurrentRequest(drawerGeneration, requestId)) {
                    return;
                }
                activeRequestIdRef.current = null;
                const message = getConsolidationErrorMessage(
                    mutationFailure,
                    "Failed to delete eliminating entry",
                );
                setMutationError(message);
                setShowDeleteConfirm(false);
            });
    };

    // Memoized so the per-line clientIds stay stable across renders. Passing a
    // freshly-minted object (new UUIDs) on every render would make Formik's
    // enableReinitialize fire resetForm() in a loop and wipe user input.
    const initialValues = useMemoByKeys(
        () => initialFormValues(entry),
        [entry?.id, open],
    );
    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        onSubmit: () => undefined,
    });
    const validity = useMemo(
        () => getFormValidity(formik.values),
        [formik.values],
    );
    // Single source of truth for every displayed total: the grid Total row, the
    // footer indicator, and the submit gate all read the same submittable-line
    // totals, so a balanced-looking total can never coexist with a blocked
    // submit (and vice versa).
    const { debitTotal, creditTotal } = validity;
    const isPending =
        createMutation.isPending ||
        updateMutation.isPending ||
        deleteMutation.isPending;
    const handleSubmit = (mode: EliminatingEntrySubmitMode) => {
        if (
            isEditLocked ||
            isSubmitInFlightRef.current ||
            isPending ||
            !validity.canSubmit
        ) {
            return;
        }
        isSubmitInFlightRef.current = true;
        setPendingSubmitMode(mode);
        setMutationError(null);
        const body = toRequestBody(formik.values);
        const submittedDate = formik.values.date;
        const drawerGeneration = drawerGenerationRef.current;
        const requestId = ++requestSequenceRef.current;
        activeRequestIdRef.current = requestId;
        const mutationPromise = entry
            ? updateMutation.mutateAsync({ id: entry.id, body })
            : createMutation.mutateAsync(body);
        void mutationPromise
            .then(() => {
                if (!isCurrentRequest(drawerGeneration, requestId)) {
                    return;
                }
                isSubmitInFlightRef.current = false;
                activeRequestIdRef.current = null;
                setMutationError(null);
                if (!entry && mode === "save-and-new") {
                    formik.resetForm({
                        values: initialFormValues(undefined, submittedDate),
                    });
                    setPendingSubmitMode(null);
                    toast("Eliminating entry created");
                    return;
                }
                toast(
                    entry
                        ? "Eliminating entry updated"
                        : "Eliminating entry created",
                );
                onClose();
            })
            .catch((mutationFailure) => {
                if (!isCurrentRequest(drawerGeneration, requestId)) {
                    return;
                }
                const message = getConsolidationErrorMessage(
                    mutationFailure,
                    entry
                        ? "Failed to update eliminating entry"
                        : "Failed to create eliminating entry",
                );
                isSubmitInFlightRef.current = false;
                activeRequestIdRef.current = null;
                setPendingSubmitMode(null);
                setMutationError(message);
            });
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            direction="right"
            noPadding
            size="fullScreen"
            headerAddonLeft={
                <JournalEntryDrawerHeader>
                    {entry ? "Eliminating entry" : "Create eliminating entry"}
                </JournalEntryDrawerHeader>
            }
        >
            {isLoadingAccounts ? (
                <VStack padding={16} align="center">
                    <Loader inline />
                </VStack>
            ) : (
                <EliminatingEntryDrawerForm
                    values={formik.values}
                    entities={entities}
                    accountsById={accountsById}
                    accountsByEntityId={accountsByEntityId}
                    debitTotal={debitTotal}
                    creditTotal={creditTotal}
                    canSubmit={validity.canSubmit}
                    isPending={isPending}
                    isEditing={entry !== undefined}
                    isLocked={isEditLocked}
                    mutationError={mutationError}
                    pendingSubmitMode={pendingSubmitMode}
                    scope={scope}
                    scopeEntities={scopeEntities}
                    consolidationContext={consolidationContext}
                    onScopeChange={onScopeChange}
                    onDateChange={(date) =>
                        void formik.setFieldValue("date", date)
                    }
                    onReversedAtChange={(reversedAt) =>
                        void formik.setFieldValue("reversedAt", reversedAt)
                    }
                    onMemoChange={(memo) =>
                        void formik.setFieldValue("memo", memo)
                    }
                    onLinesChange={(lines) =>
                        void formik.setFieldValue("lines", lines)
                    }
                    onSubmit={handleSubmit}
                    onDelete={() => {
                        if (!isEditLocked) {
                            setShowDeleteConfirm(true);
                        }
                    }}
                />
            )}
            {entry && (
                <ConfirmationModal
                    show={showDeleteConfirm && !isEditLocked}
                    title="Delete eliminating entry?"
                    question="This removes the adjustment from the consolidated report."
                    yes="Delete"
                    yesBtnVariant="destructive"
                    confirmDisabled={isEditLocked}
                    onHide={() => setShowDeleteConfirm(false)}
                    onConfirm={handleDelete}
                />
            )}
        </Drawer>
    );
};
